<template>
  <div class="json-node">
    <template v-if="isObject">
      <span class="cursor-pointer hover:bg-(--ui-bg-elevated) rounded px-1 -mx-1 select-none" @click="toggle">
        <span class="text-(--ui-text-muted)">{{ expanded ? '▼' : '▶' }}</span>
        <span v-if="label" class="text-(--ui-text-highlighted) font-medium">
          <HighlightText :text="label" :query="searchQuery" />:
        </span>
        <span v-if="!expanded" class="text-(--ui-text-dimmed)">
          {{ '{' }} {{ objectKeys.length }} {{ objectKeys.length === 1 ? 'key' : 'keys' }} {{ '}' }}
        </span>
        <span v-else class="text-(--ui-text-dimmed)">{{ '{' }}</span>
      </span>
      
      <div v-if="expanded" class="pl-5 border-l border-(--ui-border) ml-2">
        <div v-for="key in objectKeys" :key="key" class="py-0.5">
          <JsonNode
            ref="childRefs"
            :value="(value as Record<string, unknown>)[key]"
            :label="key"
            :depth="depth + 1"
            :search-query="searchQuery"
            :default-expand-depth="defaultExpandDepth"
          />
        </div>
      </div>
      
      <span v-if="expanded" class="text-(--ui-text-dimmed)">{{ '}' }}</span>
    </template>
    
    <template v-else-if="isArray">
      <span class="cursor-pointer hover:bg-(--ui-bg-elevated) rounded px-1 -mx-1 select-none" @click="toggle">
        <span class="text-(--ui-text-muted)">{{ expanded ? '▼' : '▶' }}</span>
        <span v-if="label" class="text-(--ui-text-highlighted) font-medium">
          <HighlightText :text="label" :query="searchQuery" />:
        </span>
        <span v-if="!expanded" class="text-(--ui-text-dimmed)">[{{ arrayValue.length }} {{ arrayValue.length === 1 ? 'item' : 'items' }}]</span>
        <span v-else class="text-(--ui-text-dimmed)">[</span>
      </span>
      
      <div v-if="expanded" class="pl-5 border-l border-(--ui-border) ml-2">
        <div v-for="(item, index) in arrayValue" :key="index" class="py-0.5">
          <JsonNode
            ref="childRefs"
            :value="item"
            :label="String(index)"
            :depth="depth + 1"
            :search-query="searchQuery"
            :default-expand-depth="defaultExpandDepth"
          />
        </div>
      </div>
      
      <span v-if="expanded" class="text-(--ui-text-dimmed)">]</span>
    </template>
    
    <template v-else>
      <span>
        <span v-if="label" class="text-(--ui-text-highlighted) font-medium">
          <HighlightText :text="label" :query="searchQuery" />:
        </span>
        
        <span :class="primitiveClass">
          <HighlightText :text="primitiveDisplay" :query="searchQuery" />
        </span>
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import HighlightText from '@/components/nols/HighlightText.vue'

const props = withDefaults(defineProps<{
  value: unknown
  label?: string
  depth?: number
  searchQuery?: string
  defaultExpandDepth?: number
}>(), {
  depth: 0,
  searchQuery: '',
  defaultExpandDepth: 2
})

const hasDescendantMatch = (val: unknown, query: string): boolean => {
  if (val === null || val === undefined) {
    return 'null'.includes(query)
  }
  
  if (typeof val === 'object') {
    if (Array.isArray(val)) {
      return val.some(item => hasDescendantMatch(item, query))
    }
    
    const obj = val as Record<string, unknown>
    
    return Object.entries(obj).some(([key, v]) => key.toLowerCase().includes(query) || hasDescendantMatch(v, query))
  }
  
  return String(val).toLowerCase().includes(query)
}

const shouldExpandForSearch = props.searchQuery ? hasDescendantMatch(props.value, props.searchQuery) : false

const expanded = ref(props.depth < props.defaultExpandDepth || shouldExpandForSearch)
const childRefs = ref<any[]>([])

const isObject = computed(() =>
  props.value !== null && typeof props.value === 'object' && !Array.isArray(props.value)
)

const isArray = computed(() => Array.isArray(props.value))

const objectKeys = computed(() =>
  isObject.value ? Object.keys(props.value as Record<string, unknown>) : []
)

const arrayValue = computed(() =>
  isArray.value ? (props.value as unknown[]) : []
)

const primitiveClass = computed(() => {
  if (props.value === null) return 'text-gray-400 italic'
  if (typeof props.value === 'string') return 'text-green-600 dark:text-green-400'
  if (typeof props.value === 'number') return 'text-blue-600 dark:text-blue-400'
  if (typeof props.value === 'boolean') return 'text-orange-600 dark:text-orange-400'
  
  return 'text-(--ui-text)'
})

const primitiveDisplay = computed(() => {
  if (props.value === null) return 'null'
  if (typeof props.value === 'string') return JSON.stringify(props.value)
  
  return String(props.value)
})

const toggle = () => {
  expanded.value = !expanded.value
}

watch(() => props.searchQuery, (query) => {
  if (!query) return
  
  if (hasDescendantMatch(props.value, query)) {
    expanded.value = true
  }
})

const setExpandedAll = (expand: boolean) => {
  expanded.value = expand
  
  nextTick(() => {
    if (childRefs.value) {
      const refs = Array.isArray(childRefs.value) ? childRefs.value : [childRefs.value]
      
      refs.forEach(child => {
        if (child?.setExpandedAll) {
          child.setExpandedAll(expand)
        }
      })
    }
  })
}

defineExpose({
  setExpandedAll
})
</script>